import {
  Box,
  Divider,
  Typography,
  makeStyles,
  useTheme
} from '@material-ui/core';
import {
  SText,
  SCheckbox,
  SETVIImage,
  SHighlightText
} from '@setvi/shared/components';
import { TextColor } from '@setvi/shared/enums';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { ImagePreview } from 'componentsV2';

const useStyles = makeStyles(() => ({
  itemRow: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    minHeight: 60
  },
  checkboxWrapper: {
    height: 60,
    display: 'flex',
    alignItems: 'center',
    flexShrink: 0
  },
  imageColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flexShrink: 0
  },
  badgePill: {
    marginTop: 4,
    backgroundColor: '#F8F9FA',
    borderRadius: '12px',
    padding: '2px 6px',
    maxWidth: 60,
    height: 20,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  badgeText: {
    fontSize: '10px',
    fontWeight: 600,
    color: '#5D7083',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '52px'
  },
  productDetails: {
    flex: 1,
    display: 'flex',
    gap: '12px',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
    height: 60
  }
}));

interface SelectableItemRowProps {
  product: QuoteProductDTO;
  searchQuery?: string;
  selected: boolean;
  onToggle: () => void;
}

export const SelectableItemRow = ({
  product,
  searchQuery,
  selected,
  onToggle
}: SelectableItemRowProps) => {
  const classes = useStyles();
  const { palette } = useTheme();

  const imageSrc =
    product?.imageUrl ||
    product?.images?.[0]?.path ||
    '/images/product-placeholder.svg';

  const labelText = product?.customLabel || product?.labelType;
  const truncatedLabel =
    labelText && labelText.length > 10
      ? `${labelText.slice(0, 9)}..`
      : labelText;

  return (
    <Box>
      <Box className={classes.itemRow}>
        <Box className={classes.checkboxWrapper}>
          <SCheckbox checked={selected} onChange={onToggle} />
        </Box>

        <Box className={classes.imageColumn}>
          <ImagePreview
            title={
              <img
                src={imageSrc}
                alt="product-img"
                onError={e => {
                  e.currentTarget.src = '/images/product-placeholder.svg';
                }}
                style={{
                  width: 180,
                  height: 180,
                  objectFit: 'contain',
                  backgroundColor: palette.common.white,
                  borderRadius: 10,
                  padding: 20
                }}
              />
            }
            placement="right"
            arrow>
            <Box
              minWidth={60}
              minHeight={60}
              width={60}
              display="flex"
              alignItems="center"
              justifyContent="center">
              <SETVIImage
                src={imageSrc}
                alt={product?.name || 'Product'}
                width={60}
                height={60}
                radius={10}
                fit="contain"
                style={{
                  maxHeight: 60,
                  maxWidth: 60,
                  minHeight: 60,
                  zIndex: 2,
                  backgroundColor: '#FFFFFF',
                  border: '1px solid #AFBAC6'
                }}
              />
            </Box>
          </ImagePreview>
        </Box>

        <Box className={classes.productDetails}>
          <SHighlightText
            value={product.name}
            weight="semibold"
            searchQuery={searchQuery}
            maxLines={2}
          />
          <Box display="flex" alignItems="center" gridGap={5}>
            <SHighlightText
              size="sm"
              fontColor={TextColor.SlateGrey}
              value={`SKU: ${product.sku}`}
              searchQuery={searchQuery}
            />
            <Divider orientation="vertical" flexItem />
            <SHighlightText
              size="sm"
              fontColor={TextColor.SlateGrey}
              value={`MFG: ${product?.vendor?.name || 'N/A'}`}
              searchQuery={searchQuery}
            />
          </Box>
        </Box>
      </Box>

      {(truncatedLabel || !!product?.note?.trim()) && (
        <Box
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 8,
            marginLeft: 40,
            marginTop: 4
          }}>
          <Box
            style={{
              width: 60,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'center'
            }}>
            {truncatedLabel && (
              <Box className={classes.badgePill} style={{ marginTop: 0 }}>
                <Typography className={classes.badgeText}>
                  {truncatedLabel.charAt(0).toUpperCase() +
                    truncatedLabel.slice(1)}
                </Typography>
              </Box>
            )}
          </Box>
          {!!product?.note?.trim() && (
            <Box style={{ flex: 1, overflow: 'hidden' }}>
              <Divider />
              <SText
                prefix="Note: "
                title={product.note}
                fontColor="#333D47"
                maxLines={2}
                style={{ padding: '8px 0 0' }}
              />
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};
